import { type SavedJobFilterStore } from '@/features/jobs/list/SavedJobFilterStore'
import { localStorage } from '@/infra/storage'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { PanelWidth } from '@/infra/PanelWidth'
import type {
  ListUserTeamMembersFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceSelectionFn,
} from '@/features/iam'
import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { type JobStatusFilterOption } from '@/features/jobs/list/job-status-filter-option'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  EMPTY,
  Observable,
  defer,
  distinctUntilChanged,
  filter,
  first,
  merge,
  switchMap,
  tap,
} from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import type { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { whileActive } from '@/utils/rx-util'
import { type SelectFilter } from '@/infra/SelectFilter'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import { type JobSearchViewModelFactory } from '@/features/jobs/list/JobSearchViewModel'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type JobTicketStore } from '@/features/jobs/tickets/JobTicketStore'
import {
  type GetActiveJobFn,
  JobItemViewModel,
} from '@/features/jobs/details/components/JobList/JobItem/JobItemViewModel'

/**
 * Job list view model.
 */
export class JobListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether the list is open. In the refreshed design it stays hidden until
   * the user opens it, then is remembered, so moving between jobs never
   * reflows the page around a list they didn't ask for.
   */
  readonly panel = new ModalViewModel()

  /**
   * The width of the list in the refreshed design. The default fits the saved
   * filter tabs on one line.
   */
  readonly panelWidth = new PanelWidth(localStorage, widthKey, {
    defaultWidth: 344,
    minWidth: 280,
    maxWidth: 520,
  })

  /**
   * Whether we can view opportunities.
   * Used to render the appropriate empty state.
   */
  @observable accessor canViewOpportunities = false

  /**
   * The current loaded active job's workspace.
   */
  private readonly _activeJobWorkspace$ = toStream(
    () => this._getActiveJob()?.workspace,
    true,
  ).pipe(filter(Boolean), distinctUntilChanged())

  private readonly _jobItems = new ViewModelList({
    models: () => this.searcher.jobs.slice(),
    create: (job: SearchableJob) =>
      new JobItemViewModel(
        job,
        this._getActiveJob,
        this._getCurrentUserWorkspaceMemberships,
        () => this._jobTicketStore.getLatestTicketForJob(job.id),
      ),
  })

  /**
   * Job searcher.
   */
  readonly searcher

  constructor(
    private readonly _jobTicketStore: JobTicketStore,
    createJobSearchViewModel: JobSearchViewModelFactory,
    private readonly _listUserTeamMembers: ListUserTeamMembersFn,
    private readonly _getActiveJob: GetActiveJobFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _probeCanViewOpportunities: () => Promise<boolean>,
    createFilterStorage: FilterStorageFactory,
    savedFilterStore: SavedJobFilterStore,
    private readonly _canEditDraftJobs: () => boolean,
    private readonly _searchableJobStore: SearchableJobStore,
    private readonly _getWorkspaceSelection: GetWorkspaceSelectionFn,
    private readonly _isRefreshDesign: boolean,
  ) {
    this.searcher = createJobSearchViewModel({
      filterStorage: createFilterStorage('JobList'),
      savedFilterStore,
    })

    if (!this._isRefreshDesign || readVisible()) {
      this.panel.show()
    }
    this._lifecycle.manage(this._jobItems)
    this._lifecycle.manage(
      defer(() =>
        merge(this._prependCreatedJobs(), this._activateSearcher()),
      ).pipe(
        // Not fired immediately: a resolved `fetch` from the previous
        // activation would otherwise start the searcher before the new one.
        whileActive(toStream(() => this.fetch.resolved)),
      ),
    )
  }

  /**
   * Job item view models.
   */
  get jobs() {
    return this._jobItems.items
  }

  /**
   * Whether the search finished without finding any jobs.
   */
  @computed
  get showEmptyState() {
    return (
      !this.searcher.scroller.fetch.pending &&
      this.jobs.length === 0 &&
      this.searcher.scroller.isDone
    )
  }

  /**
   * Whether we can edit draft jobs as an admin.
   */
  get canEditDraftJobsAsAdmin() {
    return this._canEditDraftJobs()
  }

  /**
   * The job `offset` places before (-1) or after (1) the open one in the
   * loaded list, if there is one.
   */
  adjacentJobId(offset: -1 | 1): string | null {
    const active = this._getActiveJob()
    if (!active) {
      return null
    }
    const jobs = this.searcher.jobs
    const index = jobs.findIndex((j) => j.id === active.id)
    if (index === -1) {
      return null
    }
    return jobs[index + offset]?.id ?? null
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The list renders a failure from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Toggles the visibility of the list.
   */
  @action.bound
  toggleVisibility() {
    if (this.panel.showing) {
      this.panel.close()
    } else {
      this.panel.show()
    }
    if (!this._isRefreshDesign) {
      return
    }
    localStorage.setItem(visibleKey, String(this.panel.showing))
  }

  /**
   * Applies a saved filter and remembers it for the next visit.
   */
  @action.bound
  selectSavedFilter(id: string) {
    this.searcher.selectSavedFilter(id)
    localStorage.setItem(savedFilterKey, id)
  }

  /**
   * Loads what the list needs before it can search.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._listUserTeamMembers(undefined, signal)
    const canViewOpportunities = await this._probeCanViewOpportunities()
    signal.throwIfAborted()
    this._setCanViewOpportunities(canViewOpportunities)

    if (this._isRefreshDesign && this.searcher.savedFilterId === null) {
      this.searcher.selectSavedFilter(localStorage.getItem(savedFilterKey))
    }
  })

  /**
   * Searches jobs.
   */
  @action.bound
  search(searchTerm: string) {
    const activeJob = this._getActiveJob()

    if (activeJob) {
      this._setDefaultWorkspaceFilter(activeJob.workspace.id, searchTerm)
    }

    this.searcher.searchText.change(searchTerm)
  }

  /**
   * When a job is created, add it to the top of the list.
   * This is a workaround for the fact that the job searcher is not
   * processing the JobCreate event when filters are applied.
   */
  private _prependCreatedJobs() {
    if (!this.canEditDraftJobsAsAdmin) {
      return EMPTY
    }

    return this._searchableJobStore.jobEvents$.pipe(
      filter((e) => e.type === 'JobCreated'),
      tap((e) => {
        // We don't want to add the job to the list if it's already there
        // or if the user is searching for something.
        if (
          this.searcher.scroller.items.includes(e.job) ||
          this.searcher.searchText.value !== ''
        ) {
          return
        }

        this.searcher.scroller.prepend(e.job)
      }),
    )
  }

  /**
   * Keeps the searcher active once the active job tells us the workspace
   * to filter by.
   */
  private _activateSearcher() {
    return this._activeJobWorkspace$.pipe(
      first(),
      switchMap(
        (workspace) =>
          new Observable<never>(() => {
            this.searcher.activate()
            // In the refreshed design the saved filter sets the statuses.
            if (!this._isRefreshDesign) {
              this._setDefaultJobStatusFilter()
            }
            this._setDefaultWorkspaceFilter(
              workspace.id,
              this.searcher.searchText.value,
            )
            return () => this.searcher.deactivate()
          }),
      ),
    )
  }

  /**
   * Sets whether we can view opportunities.
   *
   * @param canView
   */
  @action.bound
  private _setCanViewOpportunities(canView: boolean) {
    this.canViewOpportunities = canView
  }

  /**
   * Defaults search filter by job status.
   */
  @action.bound
  private _setDefaultJobStatusFilter() {
    this.searcher.filters.addFilter('jobStatus')
    const filter = this.searcher.filters.getFilter('jobStatus') as SelectFilter<
      string,
      JobStatusFilterOption
    >
    const filterBy: JobStatusFilterOption[] = [
      'InProgress',
      'Unclaimed',
      'InfoGathering',
      'Pending',
      'OnHold',
      'Idle',
    ]

    filter.selected = this.canEditDraftJobsAsAdmin
      ? ['Onboarding', ...filterBy]
      : filterBy
  }

  /**
   * Defaults search filter by the active job's workspace if possible.
   */
  @action.bound
  private _setDefaultWorkspaceFilter(workspaceId: Id, searchTerm?: string) {
    if (this.searcher.isUserTypePro) {
      return
    }

    const workspaceFilter = this.searcher.filters.getFilter(
      'workspaces',
    ) as SelectFilter<string, Id>

    // Always default to the current workspace if the user is not a pro user.
    // Otherwise, show all jobs from all workspaces.
    workspaceFilter.selected =
      !searchTerm || !this._getWorkspaceSelection().allWorkspaces
        ? [workspaceId]
        : []
  }
}

const visibleKey = 'Job details jobs list visible'
const savedFilterKey = 'Job details jobs list saved filter'
const widthKey = 'Job details jobs list width'

function readVisible() {
  return localStorage.getItem(visibleKey) === 'true'
}
